import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Award, Users, Star, MapPin, Home as HomeIcon, Building2,
  Check, Phone, MessageCircle, ChevronRight, Plane,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { WA_LINK, CALL_LINK, SERVICES, TESTIMONIALS, FAQS } from "@/data";
import StudioGallery from "@/components/StudioGallery";

const SITE_URL = "https://rajmusclecarestudio.com";
const OG_IMAGE = "https://customer-assets.emergentagent.com/job_wellness-andheri/artifacts/hcc2ecna_change_the_logo_202604131845.png";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/bec233f6-77d3-4ad9-bc33-10ecd3b76ccf/images/8a45f6c7a7f4f5ed6eaed7496d138803cebd36d36d425e798d9d0db5ecbac0b4.png";

const fadeUp = { initial: { opacity: 0, y: 32 }, animate: { opacity: 1, y: 0 } };
const inView = { initial: { opacity: 0, y: 32 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" } };

/* ────────── HERO ────────── */
function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* BG */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-[#050505]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/60 via-transparent to-[#050505]/60" />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center pt-28 pb-20">
        {/* Airport badge */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-sm text-white"
          data-testid="hero-airport-badge"
        >
          <Plane size={14} className="text-[#FF4500]" />
          <span className="font-inter font-medium">Just 15 mins from Mumbai Airport</span>
        </motion.div>

        <motion.h1
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-5"
          data-testid="hero-heading"
        >
          Male to Male Massage
          <br />
          <span className="text-[#FF4500]">Therapist in Mumbai</span>
        </motion.h1>

        <motion.p
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-base sm:text-lg text-gray-300 font-inter mb-3 max-w-2xl mx-auto"
        >
          Exclusive Massage Therapy for Men &nbsp;|&nbsp; Private Studio • Home • Hotel Service&nbsp;•
          <br className="hidden sm:block" />
          Andheri West &amp; East &nbsp;|&nbsp; Near Mumbai Airport
        </motion.p>

        <motion.p
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="text-sm text-gray-400 font-inter mb-10 max-w-xl mx-auto"
        >
          Providing professional male to male massage services in Mumbai with home service, hotel massage
          and private studio sessions near Mumbai Airport.
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#FF4500] hover:bg-[#e63d00] text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:scale-105 shadow-lg shadow-[#FF4500]/25 text-base min-h-[56px] w-full sm:w-auto justify-center"
            data-testid="hero-book-whatsapp-btn"
          >
            <MessageCircle size={20} />
            Book on WhatsApp
          </a>
          <a
            href={CALL_LINK}
            className="flex items-center gap-2 border border-white/30 hover:border-white/60 text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:bg-white/5 text-base min-h-[56px] w-full sm:w-auto justify-center"
            data-testid="hero-call-btn"
          >
            <Phone size={20} />
            Call Now
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ────────── TRUST BAR ────────── */
function TrustBar() {
  const stats = [
    { icon: <Award size={28} className="text-[#FF4500]" />, value: "15+", label: "Years Experience" },
    { icon: <Users size={28} className="text-[#FF4500]" />, value: "1,000+", label: "Happy Clients" },
    { icon: <Star size={28} className="text-[#FF4500] fill-[#FF4500]" />, value: "4.9", label: "Google Rating" },
  ];
  return (
    <div className="bg-[#111111] border-y border-white/10" data-testid="trust-bar">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-3 gap-4 sm:gap-8">
        {stats.map((s, i) => (
          <motion.div
            key={i}
            {...inView}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left"
            data-testid={`trust-stat-${i}`}
          >
            <div className="hidden sm:block">{s.icon}</div>
            <div>
              <div className="font-playfair text-2xl sm:text-3xl font-black text-white">{s.value}</div>
              <div className="text-xs sm:text-sm text-gray-400 font-inter">{s.label}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ────────── ABOUT SNIPPET ────────── */
function AboutSnippet() {
  return (
    <section className="py-16 sm:py-20 bg-[#050505]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <motion.div {...inView} transition={{ duration: 0.7 }}>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-3 font-inter">
              About Raj S
            </p>
            <h2 className="text-3xl sm:text-4xl text-white font-black mb-6 leading-snug">
              Certified Male Massage<br />Therapist in Mumbai
            </h2>
            <p className="text-gray-300 text-base font-inter leading-relaxed mb-6">
              Hello, I'm Raj S, a certified male massage therapist with 15+ years of experience
              in Mumbai. I specialize in professional massage therapy for men, offering stress relief,
              muscle recovery, and relaxation. Service available at home, hotel rooms, and private
              studio in Andheri near Mumbai Airport.
            </p>
            <div className="flex flex-col gap-3 mb-8">
              {[
                "Certified & Experienced Therapist",
                "Professional & Discreet Service",
                "Trusted by Regular & Premium Clients",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#FF4500]/20 flex items-center justify-center">
                    <Check size={12} className="text-[#FF4500]" />
                  </span>
                  <span className="text-gray-300 text-sm font-inter">{item}</span>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-[#FF4500] font-semibold text-sm hover:gap-3 transition-all"
              data-testid="about-read-more-link"
            >
              Read More <ChevronRight size={16} />
            </Link>
          </motion.div>

          <motion.div
            {...inView}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="grid grid-cols-2 gap-4"
          >
            {[
              { val: "15+", label: "Years of Experience" },
              { val: "1K+", label: "Happy Clients" },
              { val: "100%", label: "Discreet & Confidential" },
              { val: "15 min", label: "From Mumbai Airport" },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#111111] rounded-2xl p-6 border border-white/10 text-center hover:border-[#FF4500]/30 transition-colors"
              >
                <div className="font-playfair text-3xl font-black text-[#FF4500] mb-1">{item.val}</div>
                <div className="text-gray-400 text-xs font-inter">{item.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ────────── SERVICE CARD ────────── */
function ServiceCard({ service, categoryLabel, gradient, isPremium = false }) {
  return (
    <motion.div
      className="relative rounded-3xl overflow-hidden bg-[#111111] border border-white/10 flex flex-col shadow-lg"
      style={{ background: gradient }}
      whileHover={{ scale: 1.03, y: -6 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 280, damping: 18 }}
      data-testid={`service-card-${service.name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={service.image}
          alt={service.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-black/20 to-transparent" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="inline-block w-fit mb-3 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs font-semibold text-white font-inter tracking-wide">
          {categoryLabel}
        </span>
        <h3 className="text-white text-lg font-bold leading-snug mb-1">{service.name}</h3>
        <p className="text-gray-400 text-sm font-inter mb-4 flex-1 leading-relaxed">{service.description}</p>
        <div className="flex items-center justify-between pt-3 border-t border-white/10 mt-auto">
          <span className="font-mono text-2xl font-black text-white">
            ₹{service.price.toLocaleString()}
          </span>
          {!isPremium && (
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-black text-sm font-semibold px-4 py-2 rounded-full hover:bg-gray-100 transition-colors min-h-[40px] flex items-center"
              data-testid="service-card-book-btn"
            >
              Book Now
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ────────── PRICING SECTION ────────── */
function PricingSection() {
  const categories = [
    {
      key: "therapeutic",
      label: "Therapeutic Massages",
      pill: "Therapeutic",
      gradient: "linear-gradient(160deg, rgba(67,56,202,0.18) 0%, #111111 60%)",
    },
    {
      key: "relaxation",
      label: "Relaxation Massages",
      pill: "Relaxation",
      gradient: "linear-gradient(160deg, rgba(5,150,105,0.18) 0%, #111111 60%)",
    },
    {
      key: "premium",
      label: "Premium Wellness Therapies",
      pill: "Premium",
      gradient: "linear-gradient(160deg, rgba(147,51,234,0.20) 0%, #111111 60%)",
    },
  ];

  return (
    <section id="services" className="py-16 sm:py-20 bg-[#050505]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div {...inView} transition={{ duration: 0.6 }} className="text-center mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-3 font-inter">
            Our Services
          </p>
          <h2 className="text-3xl sm:text-4xl text-white font-black mb-3">
            Massage Services &amp; Pricing
          </h2>
          <p className="text-gray-400 font-inter text-sm sm:text-base">
            Starting from ₹3,000 — Studio, Home &amp; Hotel Service in Mumbai
          </p>
        </motion.div>

        {categories.map((cat) => (
          <div key={cat.key} className="mb-14">
            <motion.h3
              {...inView}
              transition={{ duration: 0.5 }}
              className="text-xl sm:text-2xl text-white font-bold mb-6 font-playfair"
            >
              {cat.label}
            </motion.h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {SERVICES[cat.key].map((service, i) => (
                <motion.div
                  key={service.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                >
                  <ServiceCard
                    service={service}
                    categoryLabel={cat.pill}
                    gradient={cat.gradient}
                    isPremium={cat.key === "premium"}
                  />
                </motion.div>
              ))}
            </div>

            {/* Premium footer */}
            {cat.key === "premium" && (
              <motion.div
                {...inView}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-6 p-5 rounded-2xl bg-[#111111] border border-white/10 text-center"
              >
                <p className="text-gray-400 text-sm font-inter mb-4">
                  Customized premium therapies available on request. Contact for details.
                </p>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#FF4500] hover:bg-[#e63d00] text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:scale-105 min-h-[52px]"
                  data-testid="premium-enquire-btn"
                >
                  <MessageCircle size={18} />
                  Enquire on WhatsApp
                </a>
              </motion.div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ────────── SERVICE MODES ────────── */
function ServiceModes() {
  const modes = [
    {
      icon: <MapPin size={32} className="text-[#FF4500]" />,
      title: "Private Studio",
      desc: "Visit our private studio in Andheri West, just 15 minutes from Mumbai Airport. Fully equipped, calm and discreet.",
      highlight: "Andheri West",
    },
    {
      icon: <HomeIcon size={32} className="text-[#FF4500]" />,
      title: "Home Service",
      desc: "We come to you. Professional massage service available across Mumbai — at your home, at your convenience.",
      highlight: "Across Mumbai",
    },
    {
      icon: <Building2 size={32} className="text-[#FF4500]" />,
      title: "Hotel Service",
      desc: "In-room massage service available near Mumbai Airport and Andheri. Late night service available on request.",
      highlight: "Near Airport",
    },
  ];

  return (
    <section className="py-16 sm:py-20 bg-[#0a0a0a]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div {...inView} transition={{ duration: 0.6 }} className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-3 font-inter">
            How to Book
          </p>
          <h2 className="text-3xl sm:text-4xl text-white font-black">We Come to You</h2>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {modes.map((mode, i) => (
            <motion.div
              key={mode.title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="bg-[#111111] rounded-3xl p-7 border border-white/10 hover:border-[#FF4500]/30 transition-colors group"
              data-testid={`service-mode-${mode.title.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="mb-5">{mode.icon}</div>
              <h3 className="text-white text-xl font-bold mb-2">{mode.title}</h3>
              <p className="text-gray-400 text-sm font-inter leading-relaxed mb-4">{mode.desc}</p>
              <span className="inline-block px-3 py-1 rounded-full bg-[#FF4500]/10 border border-[#FF4500]/20 text-[#FF4500] text-xs font-semibold font-inter">
                {mode.highlight}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────── SERVICE AREAS ────────── */
function ServiceAreas() {
  const areas = [
    "Andheri East",
    "Andheri West",
    "Mumbai Airport Area",
    "Home Massage Across Mumbai",
    "Hotel Massage Near Mumbai Airport",
    "Outcall Massage in Andheri East & West",
  ];
  return (
    <section className="py-14 bg-[#050505] border-y border-white/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div {...inView} transition={{ duration: 0.6 }} className="text-center mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-2 font-inter">
            Coverage
          </p>
          <h2 className="text-2xl sm:text-3xl text-white font-black">Service Areas</h2>
        </motion.div>
        <motion.div
          {...inView}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-3"
          data-testid="service-areas"
        >
          {areas.map((area) => (
            <span
              key={area}
              className="px-4 py-2 rounded-full bg-[#111111] border border-white/10 text-gray-300 text-sm font-inter hover:border-[#FF4500]/40 hover:text-white transition-colors"
            >
              {area}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ────────── TESTIMONIALS ────────── */
function TestimonialColumn({ items, duration = 20 }) {
  const doubled = [...items, ...items];
  return (
    <div
      className="relative overflow-hidden flex-1"
      style={{
        maskImage: "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
        WebkitMaskImage: "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
      }}
    >
      <motion.div
        className="flex flex-col gap-4"
        animate={{ translateY: ["0%", "-50%"] }}
        transition={{ duration, repeat: Infinity, ease: "linear", repeatType: "loop" }}
      >
        {doubled.map((t, i) => (
          <div
            key={i}
            className="bg-[#111111] rounded-3xl border border-white/5 shadow-lg p-6"
          >
            <div className="flex gap-0.5 mb-3">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  size={14}
                  className={
                    s <= Math.floor(t.rating)
                      ? "text-[#FF4500] fill-[#FF4500]"
                      : t.rating === s - 0.5
                      ? "text-[#FF4500] fill-[#FF4500]/40"
                      : "text-gray-600"
                  }
                />
              ))}
            </div>
            <p className="text-gray-300 text-sm font-inter leading-relaxed">"{t.text}"</p>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function TestimonialsSection() {
  const col1 = TESTIMONIALS.slice(0, 2);
  const col2 = TESTIMONIALS.slice(1, 3);
  const col3 = TESTIMONIALS.slice(2, 4);

  return (
    <section className="py-16 sm:py-20 bg-[#050505]" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div {...inView} transition={{ duration: 0.6 }} className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-3 font-inter">
            Reviews
          </p>
          <h2 className="text-3xl sm:text-4xl text-white font-black mb-3">
            What Our Clients Say
          </h2>
          <p className="text-gray-400 font-inter text-sm">
            Trusted by 1,000+ clients across Mumbai
          </p>
        </motion.div>

        {/* Scrolling Columns */}
        <div
          className="flex gap-4 max-h-[540px] overflow-hidden"
          style={{
            maskImage: "linear-gradient(to bottom, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <TestimonialColumn items={col1} duration={14} />
          <div className="hidden sm:flex flex-1 overflow-hidden relative"
            style={{
              maskImage: "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
            }}
          >
            <motion.div
              className="flex flex-col gap-4 flex-1"
              animate={{ translateY: ["0%", "-50%"] }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear", repeatType: "loop" }}
            >
              {[...col2, ...col2].map((t, i) => (
                <div key={i} className="bg-[#111111] rounded-3xl border border-white/5 shadow-lg p-6">
                  <div className="flex gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={14}
                        className={s <= Math.floor(t.rating) ? "text-[#FF4500] fill-[#FF4500]" : t.rating === s - 0.5 ? "text-[#FF4500] fill-[#FF4500]/40" : "text-gray-600"}
                      />
                    ))}
                  </div>
                  <p className="text-gray-300 text-sm font-inter leading-relaxed">"{t.text}"</p>
                </div>
              ))}
            </motion.div>
          </div>
          <div className="hidden lg:flex flex-1 overflow-hidden relative"
            style={{
              maskImage: "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent, black 12%, black 88%, transparent)",
            }}
          >
            <motion.div
              className="flex flex-col gap-4 flex-1"
              animate={{ translateY: ["0%", "-50%"] }}
              transition={{ duration: 16, repeat: Infinity, ease: "linear", repeatType: "loop" }}
            >
              {[...col3, ...col3].map((t, i) => (
                <div key={i} className="bg-[#111111] rounded-3xl border border-white/5 shadow-lg p-6">
                  <div className="flex gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={14}
                        className={s <= Math.floor(t.rating) ? "text-[#FF4500] fill-[#FF4500]" : t.rating === s - 0.5 ? "text-[#FF4500] fill-[#FF4500]/40" : "text-gray-600"}
                      />
                    ))}
                  </div>
                  <p className="text-gray-300 text-sm font-inter leading-relaxed">"{t.text}"</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        <motion.div
          {...inView}
          transition={{ duration: 0.5 }}
          className="text-center mt-8"
        >
          <a
            href="https://g.co/kgs/vFKFnSw"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#FF4500] hover:underline text-sm font-semibold font-inter"
            data-testid="google-reviews-link"
          >
            See all reviews on Google <ChevronRight size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ────────── FAQ ────────── */
function FAQSection() {
  return (
    <section className="py-16 sm:py-20 bg-[#0a0a0a]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <motion.div {...inView} transition={{ duration: 0.6 }} className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-3 font-inter">
            FAQ
          </p>
          <h2 className="text-3xl sm:text-4xl text-white font-black">Frequently Asked Questions</h2>
        </motion.div>
        <motion.div
          {...inView}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-[#111111] rounded-2xl border border-white/10 shadow-lg px-6 py-3"
        >
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-dashed border-white/10"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-white hover:text-[#FF4500] text-left hover:no-underline text-sm sm:text-base py-5 font-inter font-medium">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 text-sm font-inter leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}

/* ────────── BOOKING CTA ────────── */
function BookingCTA() {
  return (
    <section className="py-16 sm:py-20 relative overflow-hidden" data-testid="booking-cta-section">
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF4500]/10 via-[#050505] to-[#050505]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#FF4500]/8 rounded-full blur-3xl" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <motion.div {...inView} transition={{ duration: 0.6 }}>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-4 font-inter">
            Book Now
          </p>
          <h2 className="text-3xl sm:text-4xl text-white font-black mb-5">
            Book Your Massage Session in Mumbai
          </h2>
          <div className="flex flex-wrap justify-center gap-4 mb-8 text-sm font-inter text-gray-300">
            {["Instant Booking Available", "Same Day Service", "100% Discreet & Confidential"].map(
              (item) => (
                <div key={item} className="flex items-center gap-2">
                  <Check size={14} className="text-[#FF4500]" />
                  {item}
                </div>
              )
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#FF4500] hover:bg-[#e63d00] text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:scale-105 shadow-lg shadow-[#FF4500]/20 text-base min-h-[56px] w-full sm:w-auto justify-center"
              data-testid="cta-book-whatsapp-btn"
            >
              <MessageCircle size={20} />
              Book on WhatsApp
            </a>
            <a
              href={CALL_LINK}
              className="flex items-center gap-2 border border-white/30 hover:border-white/60 text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:bg-white/5 text-base min-h-[56px] w-full sm:w-auto justify-center"
              data-testid="cta-call-btn"
            >
              <Phone size={20} />
              Call Now
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ────────── HOME PAGE ────────── */
export default function Home() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Raj Muscle Care Studio | Male Massage Therapist in Mumbai | Near Airport</title>
        <meta name="description" content="Professional male massage therapist in Mumbai. Deep tissue, Swedish, Thai & premium massage for men. Private studio, home & hotel service in Andheri near Mumbai Airport. Book on WhatsApp." />
        <meta name="keywords" content="male massage therapist Mumbai, male to male massage Mumbai, massage near Mumbai airport, home massage Mumbai, hotel massage Andheri, deep tissue massage Mumbai, massage for men Mumbai" />
        <link rel="canonical" href={SITE_URL} />
        <meta property="og:title" content="Raj Muscle Care Studio | Male Massage Therapist in Mumbai" />
        <meta property="og:description" content="Professional male massage therapist in Mumbai. Studio, home & hotel service in Andheri near Mumbai Airport." />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <HeroSection />
      <TrustBar />
      <AboutSnippet />
      <PricingSection />
      <StudioGallery />
      <ServiceModes />
      <ServiceAreas />
      <TestimonialsSection />
      <FAQSection />
      <BookingCTA />
    </>
  );
}
